import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  Image,
  useToast,
  Icon,
  Badge,
} from '@chakra-ui/react';
import { FaEdit } from 'react-icons/fa';
import { updateScambio } from '../../utils/scambiApi';

export default function EditScambioModal({
  isOpen,
  onClose,
  onScambioUpdated,
  scambio,
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    note: '',
    stato: 'attivo',
  });

  useEffect(() => {
    if (scambio) {
      setFormData({
        note: scambio.note || '',
        stato: scambio.stato || 'attivo',
      });
    }
  }, [scambio]);

  const handleSubmit = async () => {
    if (!scambio) return;

    setLoading(true);
    try {
      await updateScambio(scambio.documentId, formData);

      onScambioUpdated();
      onClose();
      toast({
        title: 'Scambio aggiornato',
        description: 'Lo scambio è stato aggiornato con successo',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile aggiornare lo scambio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!scambio) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={FaEdit} color="blue.500" />
            <Text>Modifica Scambio</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Card Preview */}
            <Box
              p={4}
              bg="blue.50"
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
            >
              <HStack spacing={4}>
                <Image
                  src={
                    scambio.carte?.image_uris?.normal ||
                    scambio.carte?.image_uris?.small ||
                    '/default-card-image.png'
                  }
                  alt={scambio.carte?.nome || 'Carta'}
                  borderRadius="md"
                  boxSize="80px"
                  objectFit="cover"
                  fallbackSrc="/default-card-image.png"
                />
                <Box>
                  <Text fontSize="lg" fontWeight="bold" color="gray.800">
                    {scambio.carte?.nome || 'Carta senza nome'}
                  </Text>
                  {scambio.carte?.set_name && (
                    <Badge colorScheme="blue" mt={1}>
                      {scambio.carte.set_name}
                    </Badge>
                  )}
                  {scambio.carte?.rarity && (
                    <Badge colorScheme="purple" mt={1} ml={2}>
                      {scambio.carte.rarity}
                    </Badge>
                  )}
                </Box>
              </HStack>
            </Box>

            {/* Form Fields */}

            <FormControl>
              <FormLabel>Note aggiuntive</FormLabel>
              <Textarea
                value={formData.note}
                onChange={(e) =>
                  setFormData({ ...formData, note: e.target.value })
                }
                placeholder="Aggiungi note, condizioni, preferenze..."
                rows={4}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Stato attuale</FormLabel>
              <Select
                value={formData.stato}
                onChange={(e) =>
                  setFormData({ ...formData, stato: e.target.value })
                }
              >
                <option value="attivo">🟢 Attivo</option>
                <option value="in-trattativa">🟡 In Trattativa</option>
                <option value="completato">✅ Completato</option>
                <option value="annullato">❌ Annullato</option>
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Aggiornando..."
              leftIcon={<FaEdit />}
            >
              Aggiorna Scambio
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
